import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Plus, X, Trash2, ChevronLeft, ChevronRight, ChevronRight as Caret,
  Users, CalendarDays, Loader2, Repeat, Plane, Trophy, GraduationCap, Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';
import { resolveMediaUrl } from '../../../utils/helpers';

/**
 * Staff schedule — your own week, and a drill-down into the people who report
 * to you.
 *
 * This is deliberately NOT one calendar showing every staff member. You open on
 * yourself; your direct reports are listed beside the calendar; clicking one
 * opens their week and pushes a breadcrumb. Peers are not reachable at all, and
 * the server enforces that independently — the drill-down is the navigation, not
 * the security.
 *
 * What lands on the calendar comes from three places:
 *   - entries      — created here, by the person or their manager
 *   - classes      — derived from the squads they coach; read-only here
 *   - tournaments  — derived from tournament duties; read-only here
 *
 * Classes arrive as instants and are rendered in the viewer's timezone; manual
 * entries are academy-local wall clock and are placed literally. See the API's
 * staffScheduleController for why the two differ.
 */

const DAY_CODES  = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DAY_LABELS = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };
const WEEK_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// ── Date helpers ──────────────────────────────────────────────────────────────
// 'YYYY-MM-DD' strings throughout, built from local date parts. Using
// toISOString() here would shift the day for anyone west of UTC after 5pm.

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// 24-hour 'HH:MM' in the viewer's timezone — what <input type="time"> expects.
function clock24(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function startOfDay(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function shiftDays(d, n) {
  const out = startOfDay(d);
  out.setDate(out.getDate() + n);
  return out;
}

// Monday of the week containing d. Weeks start Monday throughout — a coaching
// week runs Mon–Sun, and splitting the weekend across two rows makes a roster
// harder to read.
function mondayOf(d) {
  const dow = startOfDay(d).getDay();          // 0 = Sunday
  return shiftDays(d, -(dow === 0 ? 6 : dow - 1));
}

function dayMeta(d, { muted = false } = {}) {
  const key = dateKey(d);
  return {
    key,
    code: DAY_CODES[d.getDay()],
    label: DAY_LABELS[DAY_CODES[d.getDay()]],
    dayNum: d.getDate(),
    display: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    long: d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }),
    isToday: key === dateKey(new Date()),
    // Month view pads to whole weeks; the padding days belong to the
    // neighbouring month and are dimmed rather than blanked, because a class on
    // the 1st is still worth seeing from the previous month's last row.
    muted,
  };
}

/**
 * The days one view covers. Every view is just a list of days, so the same
 * fetch, the same day-map and the same item rendering serve all three.
 */
function buildDays(view, anchor) {
  if (view === 'day') return [dayMeta(anchor)];

  if (view === 'week') {
    const monday = mondayOf(anchor);
    return Array.from({ length: 7 }, (_, i) => dayMeta(shiftDays(monday, i)));
  }

  // Month, padded out to whole Mon–Sun weeks: 28 to 42 days, comfortably inside
  // the API's 120-day window cap.
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const lastOfMonth  = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const gridStart = mondayOf(firstOfMonth);
  const gridEnd   = shiftDays(mondayOf(lastOfMonth), 6);
  const total = Math.round((gridEnd - gridStart) / 86400000) + 1;
  return Array.from({ length: total }, (_, i) => {
    const d = shiftDays(gridStart, i);
    return dayMeta(d, { muted: d.getMonth() !== anchor.getMonth() });
  });
}

function rangeLabel(view, anchor, days) {
  if (view === 'day')  return days[0].long;
  if (view === 'week') return `${days[0].display} – ${days[days.length - 1].display}`;
  return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// Whether the view is showing the period that contains today, which is what
// decides between "This week" and a "Back to today" escape hatch.
function isCurrentPeriod(view, anchor) {
  const today = new Date();
  if (view === 'day')  return dateKey(anchor) === dateKey(today);
  if (view === 'week') return dateKey(mondayOf(anchor)) === dateKey(mondayOf(today));
  return anchor.getFullYear() === today.getFullYear() && anchor.getMonth() === today.getMonth();
}

const CURRENT_LABEL = { day: 'Today', week: 'This week', month: 'This month' };
const BACK_LABEL    = { day: 'Back to today', week: 'Back to this week', month: 'Back to this month' };

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

function initials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

// ── Item colouring ────────────────────────────────────────────────────────────
// Four item types, and they must be distinguishable at a glance. Only `theme`
// tokens are used for surfaces and text; the small left-edge accent is the one
// place a literal colour appears, because the theme exposes no per-category
// palette and a four-way distinction cannot be drawn from one accent token.
const ITEM_STYLE = {
  event:      { accent: '#3b82f6', icon: CalendarDays,   label: 'Event' },
  time_off:   { accent: '#f59e0b', icon: Plane,          label: 'Time off' },
  class:      { accent: '#10b981', icon: GraduationCap,  label: 'Class' },
  tournament: { accent: '#8b5cf6', icon: Trophy,         label: 'Tournament' },
};
const styleFor = (kind) => ITEM_STYLE[kind] || ITEM_STYLE.event;

/**
 * Flatten the three server collections into one item-per-day map.
 *
 * Manual entries may span days (a week of leave), so they are painted onto every
 * day they cover within the visible week — otherwise a Monday–Friday absence
 * would show only on Monday.
 */
function buildDayMap(schedule, days) {
  const map = Object.fromEntries(days.map(d => [d.key, []]));
  const inWeek = (key) => key in map;

  // Entries and classes are both instants now that entries are stored in
  // `events`, so both are placed the same way: resolve to the viewer's local
  // date and paint across every day they cover.
  for (const e of schedule.entries || []) {
    const start = new Date(e.startsAt);
    const end   = e.endsAt ? new Date(e.endsAt) : start;
    const first = dateKey(start);
    const last  = dateKey(end);
    for (const d of days) {
      if (d.key >= first && d.key <= last) {
        map[d.key].push({
          ...e,
          date: first,
          endDate: last,
          sortKey: e.allDay
            ? '00:00'
            : `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
          startTime: e.allDay ? null : start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
          endTime:   e.allDay ? null : end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
          // Raw 24-hour values for the edit form — the display strings above are
          // localised ("07:00 AM") and an <input type="time"> rejects them.
          formStartTime: clock24(start),
          formEndTime:   clock24(end),
          spanning: first !== last,
        });
      }
    }
  }

  for (const c of schedule.classes || []) {
    const start = new Date(c.startsAt);
    const key = dateKey(start);
    if (!inWeek(key)) continue;
    map[key].push({
      ...c,
      kind: 'class',
      editable: false,
      sortKey: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
      startTime: start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
      endTime: c.endsAt ? new Date(c.endsAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null,
      subtitle: [c.programName, c.squadName].filter(Boolean).join(' · ') || null,
    });
  }

  for (const t of schedule.tournaments || []) {
    for (const d of days) {
      if (d.key >= t.date && d.key <= (t.endDate || t.date)) {
        map[d.key].push({
          ...t,
          kind: 'tournament',
          editable: false,
          allDay: true,
          sortKey: '00:00',
          subtitle: t.roles?.length ? t.roles.join(', ') : null,
        });
      }
    }
  }

  for (const key of Object.keys(map)) {
    map[key].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }
  return map;
}

// ── Entry modal ───────────────────────────────────────────────────────────────

const EMPTY_ENTRY = {
  entryKind: 'event',
  title: '',
  notes: '',
  allDay: false,
  startDate: '',
  endDate: '',
  startTime: '09:00',
  endTime: '10:00',
  repeatWeekly: false,
  repeatDays: [],
  repeatUntil: '',
};

const EntryModal = ({ isOpen, onClose, onSave, onDelete, initial, presetDate, staffName, isSelf, theme }) => {
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editing = Boolean(initial?.entryId);

  useEffect(() => {
    if (!isOpen) return;
    setConfirmDelete(false);
    if (initial?.entryId) {
      setForm({
        entryKind: initial.kind || 'event',
        title: initial.title || '',
        notes: initial.notes || '',
        allDay: Boolean(initial.allDay),
        // A repeating entry is a single stored series, so the form edits the
        // series — prefilling the clicked occurrence's date would march the
        // series start forward every time it was edited from a later week.
        startDate: initial.repeating ? (initial.seriesStart || initial.date) : initial.date,
        endDate: initial.endDate || initial.date,
        startTime: initial.formStartTime || '09:00',
        endTime: initial.formEndTime || '10:00',
        repeatWeekly: Boolean(initial.repeating),
        repeatDays: initial.repeatDays || [],
        repeatUntil: initial.repeatUntil || '',
      });
    } else {
      const d = presetDate || dateKey(new Date());
      setForm({ ...EMPTY_ENTRY, startDate: d, endDate: d });
    }
  }, [isOpen, initial, presetDate]);

  // Escape closes it, as every other dismissible surface in the app does.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleDay = (code) => setForm(p => ({
    ...p,
    repeatDays: p.repeatDays.includes(code)
      ? p.repeatDays.filter(c => c !== code)
      : [...p.repeatDays, code],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Give this a title'); return; }
    if (!form.allDay && form.endTime <= form.startTime) { toast.error('End time must be after start time'); return; }
    if (form.endDate < form.startDate) { toast.error('End date must not be before start date'); return; }
    if (form.repeatWeekly && !form.repeatUntil) { toast.error('A repeating entry needs an end date'); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  const inp = `w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
  const lbl = `block text-sm font-medium ${theme.text} mb-1`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className={`${theme.card} border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl`}>
        <div className={`flex items-center justify-between p-5 border-b ${theme.border}`}>
          <div className="min-w-0">
            <h3 className={`text-lg font-bold ${theme.text}`}>{editing ? 'Edit entry' : 'Add to schedule'}</h3>
            {/* Whose calendar this lands on, always — booking time on someone
                else's schedule should never be possible to do by accident. */}
            <p className={`text-xs ${theme.textMuted} mt-0.5 truncate`}>
              {isSelf ? 'Your schedule' : `${staffName}’s schedule`}
            </p>
            {/* There are no per-occurrence exceptions: one row is the whole
                series, so say so rather than letting someone believe they are
                changing a single week. */}
            {editing && form.repeatWeekly && (
              <p className="text-xs mt-1 text-amber-500">Changes apply to every occurrence in this series.</p>
            )}
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${theme.cardHover}`} aria-label="Close">
            <X size={20} className={theme.textSecondary} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className={lbl}>Type</label>
            <div className="flex gap-2">
              {[['event', 'Event'], ['time_off', 'Time off']].map(([val, label]) => {
                const active = form.entryKind === val;
                return (
                  <button key={val} type="button" onClick={() => set('entryKind', val)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      active ? `${theme.tabActive} border-transparent` : `${theme.border} ${theme.text} ${theme.cardHover}`}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={lbl}>Title <span className="text-red-500">*</span></label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)} className={inp}
              placeholder={form.entryKind === 'time_off' ? 'e.g. Annual leave' : 'e.g. Squad planning'} required />
          </div>

          <label className={`flex items-center gap-2 text-sm ${theme.text} cursor-pointer`}>
            <input type="checkbox" checked={form.allDay} onChange={e => set('allDay', e.target.checked)} className="rounded" />
            All day
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{form.repeatWeekly ? 'Starts' : 'From'}</label>
              <input type="date" value={form.startDate}
                onChange={e => { set('startDate', e.target.value); if (form.endDate < e.target.value) set('endDate', e.target.value); }}
                className={inp} required />
            </div>
            {/* A repeating entry is a series of single days, so a separate end
                date would be meaningless — repeatUntil bounds it instead. */}
            {!form.repeatWeekly && (
              <div>
                <label className={lbl}>To</label>
                <input type="date" value={form.endDate} min={form.startDate}
                  onChange={e => set('endDate', e.target.value)} className={inp} required />
              </div>
            )}
          </div>

          {!form.allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Start</label>
                <input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} className={inp} required />
              </div>
              <div>
                <label className={lbl}>End</label>
                <input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} className={inp} required />
              </div>
            </div>
          )}

          <div className={`p-3 rounded-lg border ${theme.border} space-y-3`}>
            <label className={`flex items-center gap-2 text-sm ${theme.text} cursor-pointer`}>
              <input type="checkbox" checked={form.repeatWeekly}
                onChange={e => set('repeatWeekly', e.target.checked)} className="rounded" />
              <Repeat size={14} className={theme.textMuted} /> Repeat weekly
            </label>
            {form.repeatWeekly && (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {WEEK_ORDER.map(code => {
                    const on = form.repeatDays.includes(code);
                    return (
                      <button key={code} type="button" onClick={() => toggleDay(code)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          on ? `${theme.tabActive} border-transparent` : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`}`}>
                        {DAY_LABELS[code]}
                      </button>
                    );
                  })}
                </div>
                <p className={`text-xs ${theme.textMuted}`}>
                  {form.repeatDays.length === 0 && 'No days picked — it will repeat on the start date’s weekday.'}
                </p>
                <div>
                  <label className={lbl}>Repeat until <span className="text-red-500">*</span></label>
                  <input type="date" value={form.repeatUntil} min={form.startDate}
                    onChange={e => set('repeatUntil', e.target.value)} className={inp} required />
                </div>
              </>
            )}
          </div>

          <div>
            <label className={lbl}>
              {form.entryKind === 'time_off' ? 'Reason' : 'Notes'}
              <span className={`font-normal ${theme.textMuted}`}> (optional)</span>
            </label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className={inp}
              placeholder={form.entryKind === 'time_off' ? 'Only your manager sees this' : 'Anything worth noting'} />
            {form.entryKind === 'time_off' && (
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                Visible to you and your management chain. Never shown to colleagues or on your public page.
              </p>
            )}
          </div>

          <div className={`flex gap-2 pt-2 border-t ${theme.border}`}>
            {editing && !confirmDelete && (
              <button type="button" onClick={() => setConfirmDelete(true)}
                className={`px-3 py-2 ${theme.buttonDanger} rounded-lg flex items-center`} aria-label="Delete entry">
                <Trash2 size={14} />
              </button>
            )}
            {editing && confirmDelete && (
              <button type="button" onClick={onDelete}
                className={`flex-1 px-3 py-2 ${theme.buttonDanger} rounded-lg text-sm font-medium`}>
                Confirm delete
              </button>
            )}
            {!confirmDelete && (
              <>
                <button type="button" onClick={onClose}
                  className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm`}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm font-medium disabled:opacity-60`}>
                  {saving ? 'Saving…' : editing ? 'Save' : 'Add'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Day view ──────────────────────────────────────────────────────────────────
// One day gets the room the week grid cannot spare: full times, the subtitle,
// and the note — which on a time-off entry is the reason, and is the whole point
// of a manager opening it.

const DayView = ({ day, items, canManage, onAdd, onOpen, theme }) => (
  <div className={`${theme.card} border rounded-xl overflow-hidden`}>
    <div className={`px-4 py-3 border-b ${theme.border} ${day.isToday ? theme.backgroundSecondary : ''}`}>
      <p className={`text-sm font-bold ${theme.text}`}>{day.long}</p>
      <p className={`text-xs ${theme.textMuted} mt-0.5`}>
        {items.length === 0 ? 'Nothing scheduled' : `${items.length} item${items.length === 1 ? '' : 's'}`}
      </p>
    </div>

    <div className="p-3 space-y-2">
      {items.map(item => {
        const s = styleFor(item.kind);
        const Icon = s.icon;
        const clickable = item.editable && canManage;
        return (
          <button
            key={item.id}
            onClick={() => onOpen(item)}
            disabled={!clickable}
            className={`w-full flex items-start gap-3 text-left rounded-lg px-3 py-2.5 border ${theme.border} ${theme.backgroundSecondary} ${
              clickable ? `${theme.cardHover} cursor-pointer` : 'cursor-default'} transition-colors`}
            style={{ borderLeftWidth: 3, borderLeftColor: s.accent }}
          >
            {/* Fixed-width time gutter, so the titles line up down the day. */}
            <div className="w-24 shrink-0">
              <p className={`text-xs font-semibold ${theme.text} whitespace-nowrap`}>
                {item.allDay ? 'All day' : item.startTime}
              </p>
              {!item.allDay && item.endTime && (
                <p className={`text-[11px] ${theme.textMuted} whitespace-nowrap`}>to {item.endTime}</p>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Icon size={13} style={{ color: s.accent }} className="shrink-0" />
                <p className={`text-sm font-medium ${theme.text} break-words`}>{item.title}</p>
                {item.repeating && <Repeat size={11} className={`${theme.textMuted} shrink-0`} />}
                {!item.editable && <Lock size={11} className={`${theme.textMuted} shrink-0`} />}
              </div>
              {item.subtitle && <p className={`text-xs ${theme.textMuted} mt-0.5`}>{item.subtitle}</p>}
              {item.notes && <p className={`text-xs ${theme.textSecondary} mt-1 break-words`}>{item.notes}</p>}
              {item.spanning && (
                <p className={`text-[11px] ${theme.textMuted} mt-0.5`}>Part of {item.date} – {item.endDate}</p>
              )}
            </div>
          </button>
        );
      })}

      {canManage && (
        <button
          onClick={() => onAdd(day.key)}
          className={`w-full rounded-lg border border-dashed ${theme.border} ${theme.textMuted} py-2 text-sm transition-colors flex items-center justify-center gap-1.5 ${theme.cardHover}`}
        >
          <Plus size={14} /> Add to this day
        </button>
      )}
    </div>
  </div>
);

// ── Month grid ────────────────────────────────────────────────────────────────
// A month cell has room for roughly three chips. Rather than clip silently, the
// overflow is a button that opens that day — which is what someone wanting the
// detail is reaching for anyway.

const MONTH_CHIP_LIMIT = 3;

const MonthGrid = ({ days, dayMap, canManage, onAdd, onOpen, onOpenDay, theme }) => (
  <div className={`${theme.card} border rounded-xl overflow-hidden`}>
    <div className="overflow-x-auto">
      <div style={{ minWidth: 728 }}>
        <div className="grid grid-cols-7">
          {WEEK_ORDER.map(code => (
            <div key={code} className={`px-2 py-2 text-center border-r last:border-r-0 border-b ${theme.border}`}>
              <span className={`text-xs font-bold uppercase tracking-wide ${theme.textSecondary}`}>{DAY_LABELS[code]}</span>
            </div>
          ))}
        </div>

        {chunk(days, 7).map((week, wi) => (
          <div key={wi} className={`grid grid-cols-7 ${wi > 0 ? `border-t ${theme.border}` : ''}`}>
            {week.map(day => {
              const items = dayMap[day.key] || [];
              const shown = items.slice(0, MONTH_CHIP_LIMIT);
              const hidden = items.length - shown.length;
              return (
                <div key={day.key}
                  className={`border-r last:border-r-0 ${theme.border} p-1.5 min-h-[104px] ${
                    day.isToday ? theme.backgroundSecondary : ''} ${day.muted ? 'opacity-40' : ''}`}>
                  <div className="flex items-center justify-between mb-1">
                    <button
                      onClick={() => onOpenDay(day.key)}
                      className={`text-xs font-semibold ${day.isToday ? theme.link : theme.textSecondary} hover:underline`}
                      title="Open this day"
                    >
                      {day.dayNum}
                    </button>
                    {canManage && !day.muted && (
                      <button onClick={() => onAdd(day.key)} aria-label={`Add entry on ${day.display}`}
                        className={`${theme.textMuted} rounded p-0.5 ${theme.cardHover} transition-colors`}>
                        <Plus size={11} />
                      </button>
                    )}
                  </div>

                  <div className="space-y-1">
                    {shown.map(item => {
                      const s = styleFor(item.kind);
                      const clickable = item.editable && canManage;
                      return (
                        <button
                          key={item.id}
                          onClick={() => onOpen(item)}
                          disabled={!clickable}
                          title={`${item.allDay ? 'All day' : `${item.startTime}${item.endTime ? `–${item.endTime}` : ''}`} · ${item.title}`}
                          className={`w-full text-left rounded px-1.5 py-1 ${theme.backgroundSecondary} border ${theme.border} ${
                            clickable ? `${theme.cardHover} cursor-pointer` : 'cursor-default'} transition-colors`}
                          style={{ borderLeftWidth: 3, borderLeftColor: s.accent }}
                        >
                          <p className={`text-[10px] font-semibold ${theme.textMuted} leading-tight whitespace-nowrap`}>
                            {item.allDay ? 'All day' : item.startTime}
                          </p>
                          <p className={`text-[11px] ${theme.text} leading-tight truncate`}>{item.title}</p>
                        </button>
                      );
                    })}
                    {hidden > 0 && (
                      <button onClick={() => onOpenDay(day.key)}
                        className={`text-[10px] ${theme.link} hover:underline pl-1`}>
                        +{hidden} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Week grid ─────────────────────────────────────────────────────────────────

const WeekGrid = ({ days, dayMap, canManage, onAdd, onOpen, theme }) => (
  <div className={`${theme.card} border rounded-xl overflow-hidden`}>
    <div className="overflow-x-auto">
      {/* Sized so a column fits "07:00–09:00" plus its two badge icons on one
          line, and no wider — a week view that needs a horizontal scroll to
          reach Sunday is a week view that failed. Below this the container
          scrolls rather than compressing the time into an ellipsis. */}
      <div className="grid grid-cols-7" style={{ minWidth: 728 }}>
        {days.map(day => (
          <div key={day.key} className={`border-r last:border-r-0 ${theme.border}`}>
            <div className={`px-2 py-2 text-center border-b ${theme.border} ${day.isToday ? theme.backgroundSecondary : ''}`}>
              <p className={`text-xs font-bold uppercase tracking-wide ${day.isToday ? theme.link : theme.textSecondary}`}>{day.label}</p>
              <p className={`text-xs ${day.isToday ? theme.link : theme.textMuted}`}>{day.display}</p>
            </div>

            <div className="p-1.5 space-y-1.5 min-h-[168px]">
              {dayMap[day.key].map(item => {
                const s = styleFor(item.kind);
                const Icon = s.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onOpen(item)}
                    disabled={!item.editable || !canManage}
                    className={`w-full text-left rounded-lg px-2 py-1.5 border ${theme.border} ${theme.backgroundSecondary} ${
                      item.editable && canManage ? `${theme.cardHover} cursor-pointer` : 'cursor-default'
                    } transition-colors`}
                    style={{ borderLeftWidth: 3, borderLeftColor: s.accent }}
                    title={[
                      item.allDay ? 'All day' : `${item.startTime}${item.endTime ? ` – ${item.endTime}` : ''}`,
                      item.title, item.subtitle, item.notes,
                    ].filter(Boolean).join(' · ')}
                  >
                    {/* Start time only. A localised range ("07:00 AM–09:00 AM")
                        does not fit a week column without either wrapping or
                        being clipped to "07:00 AM–…", and a clipped time is
                        worse than no time. The full range is in the tooltip, in
                        the day view, and in the edit form. */}
                    <div className="flex items-center gap-1 mb-0.5">
                      <Icon size={10} style={{ color: s.accent }} className="shrink-0" />
                      <span className={`text-[10px] font-semibold ${theme.textMuted} whitespace-nowrap`}>
                        {item.allDay ? 'All day' : item.startTime}
                      </span>
                      {item.repeating && <Repeat size={9} className={`${theme.textMuted} shrink-0`} />}
                      {!item.editable && <Lock size={9} className={`${theme.textMuted} shrink-0`} />}
                    </div>
                    <p className={`text-xs font-medium ${theme.text} leading-snug break-words`}>{item.title}</p>
                    {item.subtitle && (
                      <p className={`text-[10px] ${theme.textMuted} leading-snug truncate`}>{item.subtitle}</p>
                    )}
                  </button>
                );
              })}

              {canManage && (
                <button
                  onClick={() => onAdd(day.key)}
                  aria-label={`Add entry on ${day.display}`}
                  className={`w-full rounded-lg border border-dashed ${theme.border} ${theme.textMuted} hover:${theme.text} py-1 transition-colors flex items-center justify-center`}
                >
                  <Plus size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Main ──────────────────────────────────────────────────────────────────────

const StaffScheduleSettings = ({ academyId }) => {
  const { theme } = useTheme();

  // Which staff member's week is on screen. Null until the first reports call
  // tells us who the caller is.
  const [staffId, setStaffId] = useState(null);
  const [tree, setTree] = useState({ node: null, trail: [], reports: [] });
  const [schedule, setSchedule] = useState(null);
  // Day / week / month all reduce to "a list of days from an anchor date", so
  // one anchor plus one view name drives the whole calendar.
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ open: false, initial: null, presetDate: null });

  const days = useMemo(() => buildDays(view, anchor), [view, anchor]);
  const atCurrent = isCurrentPeriod(view, anchor);

  // Prev/Next step by whatever the current view shows.
  const step = useCallback((direction) => {
    setAnchor(prev => {
      if (view === 'day')  return shiftDays(prev, direction);
      if (view === 'week') return shiftDays(prev, direction * 7);
      return new Date(prev.getFullYear(), prev.getMonth() + direction, 1);
    });
  }, [view]);

  // Switching view keeps the anchor, so moving week → month lands on the month
  // you were already looking at rather than snapping back to today.
  const openDay = useCallback((key) => {
    const [y, m, d] = key.split('-').map(Number);
    setAnchor(new Date(y, m - 1, d));
    setView('day');
  }, []);

  // The org level. Reloaded only when the drill position changes, not when the
  // week does — walking weeks should not re-fetch the team list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await academyAPI.staffSchedule.reports(academyId, staffId ?? undefined);
        if (cancelled) return;
        setTree(data);
        if (staffId == null && data.node?.staffId) {
          setStaffId(data.node.staffId);
        } else if (staffId == null && data.reports?.length) {
          // ⚠️ THE OWNER IS NOT A STAFF ROW, so `node` comes back null for them
          // and there is no staffId of their own to open. Without this the
          // screen had nobody selected, loadSchedule bailed on every render, and
          // the panel showed "Loading schedule…" for ever — the owner of the
          // academy could never open their own staff rota.
          // They still have a team, so open on the first person in it; the
          // sidebar lets them move to anyone else.
          setStaffId(data.reports[0].staffId);
        } else if (staffId == null) {
          // Nobody to show — not staff, and no reports. Say so rather than
          // spinning.
          setLoading(false);
        }
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('[StaffSchedule] reports failed', err);
        setError(err?.message || 'Could not load your team');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [academyId, staffId]);

  const loadSchedule = useCallback(async () => {
    // Bails while there is nobody chosen yet, and deliberately leaves `loading`
    // alone: that flag means "the first load has not finished", and until the
    // reports call above says who to show, it has not. Whoever resolves who —
    // or resolves that there is nobody — is what clears it.
    if (!staffId) return;
    setLoading(true);
    try {
      const data = await academyAPI.staffSchedule.forStaff(academyId, staffId, days[0].key, days[days.length - 1].key);
      setSchedule(data);
      setError(null);
    } catch (err) {
      console.error('[StaffSchedule] schedule failed', err);
      setError(err?.message || 'Could not load this schedule');
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [academyId, staffId, days]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const dayMap = useMemo(
    () => (schedule ? buildDayMap(schedule, days) : Object.fromEntries(days.map(d => [d.key, []]))),
    [schedule, days]
  );

  const canManage = Boolean(schedule?.canManage);
  const isSelf    = Boolean(schedule?.isSelf);
  const staffName = schedule?.staff?.name || tree.node?.name || '';

  // What the toolbar's "Add entry" prefills. Today when it is on screen, else
  // the first day that actually belongs to the period — in month view days[0]
  // is usually padding from the previous month, and prefilling that would put
  // the entry outside the calendar the user is looking at.
  const defaultAddDate = useMemo(() => {
    const todayKey = dateKey(new Date());
    if (days.some(d => d.key === todayKey && !d.muted)) return todayKey;
    return (days.find(d => !d.muted) || days[0]).key;
  }, [days]);

  const closeModal = () => setModal({ open: false, initial: null, presetDate: null });
  const openAdd  = (key) => setModal({ open: true, initial: null, presetDate: key });
  const openEdit = (item) => {
    if (item.editable && canManage) setModal({ open: true, initial: item, presetDate: null });
  };

  const handleSave = async (form) => {
    const payload = {
      entryKind: form.entryKind,
      title: form.title.trim(),
      notes: form.notes?.trim() || null,
      allDay: form.allDay,
      startDate: form.startDate,
      endDate: form.repeatWeekly ? form.startDate : form.endDate,
      startTime: form.allDay ? null : form.startTime,
      endTime: form.allDay ? null : form.endTime,
      repeatWeekly: form.repeatWeekly,
      repeatDays: form.repeatWeekly ? form.repeatDays : [],
      repeatUntil: form.repeatWeekly ? form.repeatUntil : null,
    };
    try {
      if (modal.initial?.entryId) {
        await academyAPI.staffSchedule.updateEntry(academyId, modal.initial.entryId, payload);
        toast.success('Entry updated');
      } else {
        await academyAPI.staffSchedule.createEntry(academyId, { ...payload, staffId });
        toast.success('Entry added');
      }
      closeModal();
      await loadSchedule();
    } catch (err) {
      console.error('[StaffSchedule] save failed', err);
      toast.error(err?.message || 'Could not save this entry');
    }
  };

  const handleDelete = async () => {
    try {
      await academyAPI.staffSchedule.deleteEntry(academyId, modal.initial.entryId);
      toast.success('Entry removed');
      closeModal();
      await loadSchedule();
    } catch (err) {
      console.error('[StaffSchedule] delete failed', err);
      toast.error(err?.message || 'Could not remove this entry');
    }
  };

  if (error && !schedule) {
    return (
      <div className={`text-center py-16 ${theme.textMuted}`}>
        <CalendarDays size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h3 className={`text-2xl font-bold ${theme.text}`}>Staff Schedule</h3>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {isSelf
              ? `Your ${view}, including the classes you coach and any tournament duties.`
              : `${staffName}’s ${view}. You can add and edit entries for the people who report to you.`}
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => openAdd(defaultAddDate)}
            className={`flex items-center gap-1.5 px-4 py-2 ${theme.buttonPrimary} rounded-lg font-medium text-sm shrink-0`}
          >
            <Plus size={16} /> Add entry
          </button>
        )}
      </div>

      {/* ── Breadcrumb: how you got here, and the way back up ── */}
      {tree.trail.length > 1 && (
        <nav className="flex items-center gap-1 flex-wrap mb-4 text-sm" aria-label="Reporting line">
          {tree.trail.map((crumb, i) => {
            const last = i === tree.trail.length - 1;
            return (
              <React.Fragment key={crumb.staffId}>
                {i > 0 && <Caret size={13} className={theme.textMuted} />}
                <button
                  onClick={() => !last && setStaffId(crumb.staffId)}
                  disabled={last}
                  className={last ? `${theme.text} font-semibold` : `${theme.link} hover:underline`}
                >
                  {i === 0 ? 'You' : crumb.name}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {/* ── View switch ── */}
      <div className={`inline-flex rounded-lg border ${theme.border} overflow-hidden mb-3`}>
        {[['day', 'Day'], ['week', 'Week'], ['month', 'Month']].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${view === id ? theme.tabActive : theme.tabInactive}`}
            aria-pressed={view === id}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Period navigation ── */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <button onClick={() => step(-1)} aria-label={`Previous ${view}`}
          className={`flex items-center gap-1 px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition-colors`}>
          <ChevronLeft size={16} /> Prev
        </button>
        <div className="text-center min-w-0">
          <p className={`font-semibold ${theme.text} text-sm truncate`}>{rangeLabel(view, anchor, days)}</p>
          {atCurrent
            ? <p className={`text-xs ${theme.textMuted}`}>{CURRENT_LABEL[view]}</p>
            : <button onClick={() => setAnchor(startOfDay(new Date()))} className={`text-xs ${theme.link} hover:underline`}>{BACK_LABEL[view]}</button>}
        </div>
        <button onClick={() => step(1)} aria-label={`Next ${view}`}
          className={`flex items-center gap-1 px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm transition-colors`}>
          Next <ChevronRight size={16} />
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* ── Calendar ── */}
        <div className="flex-1 min-w-0">
          {loading && !schedule ? (
            <div className={`flex items-center justify-center gap-2 py-20 ${theme.textMuted}`}>
              <Loader2 size={18} className="animate-spin" /> <span className="text-sm">Loading schedule…</span>
            </div>
          ) : (
            <>
              {view === 'day' && (
                <DayView
                  day={days[0]}
                  items={dayMap[days[0].key] || []}
                  canManage={canManage}
                  onAdd={openAdd}
                  onOpen={openEdit}
                  theme={theme}
                />
              )}
              {view === 'week' && (
                <WeekGrid
                  days={days}
                  dayMap={dayMap}
                  canManage={canManage}
                  onAdd={openAdd}
                  onOpen={openEdit}
                  theme={theme}
                />
              )}
              {view === 'month' && (
                <MonthGrid
                  days={days}
                  dayMap={dayMap}
                  canManage={canManage}
                  onAdd={openAdd}
                  onOpen={openEdit}
                  onOpenDay={openDay}
                  theme={theme}
                />
              )}
              {/* A legend, because four categories that differ only by a 3px
                  accent are not self-explanatory. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                {Object.entries(ITEM_STYLE).map(([kind, s]) => (
                  <span key={kind} className={`flex items-center gap-1.5 text-xs ${theme.textMuted}`}>
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.accent }} />
                    {s.label}
                  </span>
                ))}
                <span className={`flex items-center gap-1.5 text-xs ${theme.textMuted}`}>
                  <Lock size={11} /> Comes from elsewhere — edit it at the source
                </span>
              </div>
            </>
          )}
        </div>

        {/* ── The team: one level down, never the whole org ── */}
        <div className="lg:w-64 shrink-0">
          <div className={`${theme.card} border rounded-xl p-3`}>
            <p className={`text-xs font-semibold uppercase tracking-wider ${theme.textMuted} mb-2 flex items-center gap-1.5`}>
              <Users size={13} /> Reports to {isSelf ? 'you' : staffName.split(' ')[0]}
            </p>

            {tree.reports.length === 0 ? (
              <p className={`text-xs ${theme.textMuted} py-2`}>
                Nobody reports to {isSelf ? 'you' : 'them'}.
              </p>
            ) : (
              <div className="space-y-1">
                {/* Naming the person is the whole navigation: the reports effect
                    reloads the org level from them, and the schedule effect
                    follows it. There is nothing else to reset. */}
                {tree.reports.map(person => (
                  <button
                    key={person.staffId}
                    onClick={() => setStaffId(person.staffId)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg ${theme.cardHover} transition-colors text-left ${
                      person.staffId === staffId ? theme.backgroundSecondary : ''}`}
                  >
                    {person.profileImage ? (
                      <img src={resolveMediaUrl(person.profileImage)} alt="" className="w-8 h-8 rounded-full object-cover shrink-0"
                        onError={e => { e.target.style.visibility = 'hidden'; }} />
                    ) : (
                      <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold ${theme.tabActive}`}>
                        {initials(person.name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${theme.text} truncate leading-snug`}>{person.name}</p>
                      <p className={`text-xs ${theme.textMuted} truncate leading-snug capitalize`}>{person.role}</p>
                    </div>
                    {person.hasReports && <Caret size={13} className={`${theme.textMuted} shrink-0`} />}
                  </button>
                ))}
              </div>
            )}

            <p className={`text-xs ${theme.textMuted} mt-3 pt-3 border-t ${theme.border} leading-relaxed`}>
              You can see your own schedule and everyone below you in the reporting
              line. Colleagues at your level are not shown.
            </p>
          </div>
        </div>
      </div>

      <EntryModal
        isOpen={modal.open}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
        initial={modal.initial}
        presetDate={modal.presetDate}
        staffName={staffName}
        isSelf={isSelf}
        theme={theme}
      />
    </div>
  );
};

export default StaffScheduleSettings;
